import { useCallback, useEffect, useRef } from 'react'
import {
  useFormContext,
  useWatch,
  type SubmitHandler,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { useTranslations } from '@i18n/useLanguage'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type UseBasedFormSubmitOptions = {
  /** Message shown on success. If omitted no success toast is shown. */
  successMessage?: string
  /** Message shown on failure. Defaults to the toast provider's default. */
  failMessage?: string
  /** Whether to hide all toasts. Default: false. */
  hideToast?: boolean
  /**
   * Map server-side errors back to specific form fields.
   * Receives the error and a `setError` helper with a loose signature so it
   * works regardless of the form's generic T.
   */
  onServerError?: (
    error: unknown,
    setError: (name: string, error: { type: string; message: string }) => void,
  ) => void
}

export type UseBasedAutoSaveOptions<T extends FieldValues = FieldValues> = {
  /** Debounce delay in milliseconds. Default: 500. */
  debounceMs?: number
  /** Map form values to the payload expected by the Based function. */
  getPayload?: (data: T) => unknown
  /** Called when auto-save fails. */
  onError?: (err: unknown) => void
  /** Whether to hide all toasts. Default: false. */
  hideToast?: boolean
}

export type BasedAutoSaveProps<T extends FieldValues = FieldValues> = {
  functionName: string
} & UseBasedAutoSaveOptions<T>

// ---------------------------------------------------------------------------
// useBasedFormSubmit
// ---------------------------------------------------------------------------

/**
 * Returns an `onSubmit` handler that calls a Based function with the form data.
 * Must be called inside both a `<Form>` (FormProvider) and a `ToastProvider`.
 *
 * @example
 * ```tsx
 * const handleSubmit = useBasedFormSubmit('update-profile', {
 *   successMessage: t.saved,
 * })
 *
 * <Form schema={schema} onSubmit={handleSubmit}>
 *   ...
 * </Form>
 * ```
 */
export function useBasedFormSubmit<T extends FieldValues>(
  functionName: string,
  options: UseBasedFormSubmitOptions = {},
): SubmitHandler<T> {
  const t = useTranslations().app.form.submissionResponse
  const defaultOptions = {
    successMessage: t.success,
    failMessage: t.fail,
  }
  options = { ...defaultOptions, ...options }
  const client = useClient()
  const { showSuccessToast, showFailToast } = useToast()
  const { setError } = useFormContext()

  return useCallback(
    async (data: T) => {
      try {
        await client.call(functionName, data)
        if (options.successMessage && !options.hideToast) {
          showSuccessToast(options.successMessage)
        }
      } catch (err) {
        if (options.onServerError) {
          options.onServerError(err, (name, error) =>
            setError(name as FieldPath<T>, error),
          )
        } else {
          if (!options.hideToast) {
            showFailToast(options.failMessage)
          }
        }
      }
    },
    [client, functionName, options, setError, showFailToast, showSuccessToast],
  )
}

// ---------------------------------------------------------------------------
// useBasedAutoSave
// ---------------------------------------------------------------------------

/**
 * Watches the current form values and auto-saves them to a Based function
 * with debouncing. A drop-in replacement for `useInitiativeAutoSave`.
 *
 * Must be called inside both a `<Form>` (FormProvider) and a `ToastProvider`.
 *
 * @example
 * ```tsx
 * // Inside a child of <Form>:
 * useBasedAutoSave('edit-initiative', { debounceMs: 300 })
 * ```
 */
export function useBasedAutoSave<T extends FieldValues = FieldValues>(
  functionName: string,
  options: UseBasedAutoSaveOptions<T> = {},
): void {
  const { debounceMs = 500, getPayload, onError, hideToast } = options
  const client = useClient()
  const { showFailToast } = useToast()
  const { control } = useFormContext<T>()

  const values = useWatch({ control })
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Track skipping the initial mount
  const isMountedRef = useRef(false)

  const save = useCallback(
    async (data: T) => {
      try {
        await client.call(functionName, getPayload ? getPayload(data) : data)
      } catch (err) {
        if (onError) onError(err)
        else if (!hideToast) showFailToast()
      }
    },
    [client, functionName, getPayload, onError, showFailToast, hideToast],
  )

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      void save(values as T)
    }, debounceMs)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [debounceMs, save, values])
}

export const BasedAutoSave = <T extends FieldValues = FieldValues>({
  functionName,
  ...options
}: BasedAutoSaveProps<T>): null => {
  useBasedAutoSave<T>(functionName, options)

  return null
}
